import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { Region } from '@/components/region';
import { Button } from '@/components/ui/button';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';

export type NotFoundContentProps = {
    notFoundPage: Promise<PageWithComponentData | null> | PageWithComponentData | null;
};

/**
 * 404 message, homepage CTA, and Page Designer merchandising region.
 * Rendered inside `_app` chrome (header / footer) from the layout ErrorBoundary.
 */
export default function NotFoundContent({ notFoundPage }: NotFoundContentProps): ReactElement {
    const { t } = useTranslation('routeError');

    return (
        <>
            <div
                className="flex items-center bg-neutral-white justify-center px-4 py-12 md:py-16"
                data-testid="not-found-content">
                <div className="mx-auto w-full max-w-3xl text-center">
                    <div className="text-error-status heading-headline-m mb-8 font-bold leading-none">404</div>

                    <h1 className="heading-headline-m mb-6 text-neutral-10">{t('404.title')}</h1>

                    <p className="body-copy-l mx-auto mb-4 max-w-2xl text-neutral-10">{t('404.message')}</p>
                    <p className="body-copy-l mx-auto mb-12 max-w-2xl text-neutral-10">{t('404.secondaryMessage')}</p>

                    <Button asChild hierarchy="primary" size="large" className="cursor-pointer">
                        <Link to="/">{t('goToHomepage')}</Link>
                    </Button>
                </div>
            </div>

            <div className="bg-neutral-white">
                <div className="grid-global py-8 md:py-10">
                    <div className="col-span-full lg:col-span-10 lg:col-start-2">
                        <Region page={notFoundPage} regionId="editorialContent" />
                    </div>
                </div>
            </div>
        </>
    );
}
